  if (!customElements.get('nodus-slideshow')) {
    class NodusSlideshow extends HTMLElement {
      constructor() {
        super();
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._setup());
        } else {
          requestAnimationFrame(() => this._setup());
        }
      }

      _setup() {
        this.currentIndex = 0;
        this.slides = [...this.querySelectorAll('.nodus-slideshow__slide')];
        this.indicators = [...this.querySelectorAll('.nodus-slideshow__indicator')];
        this.muteBtn = this.querySelector('.nodus-slideshow__ctrl--mute');
        this.playPauseBtn = this.querySelector('.nodus-slideshow__ctrl--playpause');
        this.shouldAutoplay = this.dataset.autoplay === 'true';
        this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.isMuted = true;
        this.isVideoPaused = false;
        this.timer = null;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this._init();
      }

      _init() {
        this.indicators.forEach((btn, i) => {
          btn.addEventListener('click', () => this.goTo(i));
        });

        if (this.muteBtn) {
          this.muteBtn.addEventListener('click', () => this._toggleMute());
        }
        if (this.playPauseBtn) {
          this.playPauseBtn.addEventListener('click', () => this._togglePlayPause());
        }

        this.mobileQuery.addEventListener('change', () => this._onResize());

        this._setupSwipe();
        this._setupSlideLinks();
        this.goTo(0, false);

        if (this.shouldAutoplay && this.slides.length > 1) {
          this._startAutoplay();
        }
      }

      goTo(index, animate = true) {
        if (index < 0) index = this.slides.length - 1;
        if (index >= this.slides.length) index = 0;

        const prev = this.currentIndex;
        this.currentIndex = index;

        this.slides.forEach((slide, i) => {
          const isActive = i === index;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));

          const vids = slide.querySelectorAll('video');
          vids.forEach(v => {
            if (isActive) {
              v.currentTime = 0;
              v.play().catch(() => {});
            } else {
              v.pause();
            }
          });
        });

        this.indicators.forEach((ind, i) => {
          ind.classList.toggle('is-active', i === index);
        });

        this._syncVideoControls();
        this._restartProgress();
      }

      _next() { this.goTo(this.currentIndex + 1); }
      _prev() { this.goTo(this.currentIndex - 1); }

      _startAutoplay() {
        this._clearAutoplay();
        this.timer = setInterval(() => this._next(), this.speed);
        this._restartProgress();
      }

      _clearAutoplay() {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
      }

      _restartProgress() {
        this.indicators.forEach((ind, i) => {
          const bar = ind.querySelector('.nodus-slideshow__indicator-progress');
          if (!bar) return;
          bar.style.transition = 'none';
          bar.style.width = '0%';

          if (i === this.currentIndex && this.shouldAutoplay && this.slides.length > 1) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                bar.style.transition = `width ${this.speed}ms linear`;
                bar.style.width = '100%';
              });
            });
          }
        });
      }

      _getCurrentVideos() {
        const slide = this.slides[this.currentIndex];
        if (!slide) return [];
        const isMobile = this.mobileQuery.matches;
        const mediaClass = isMobile ? '.nodus-slideshow__media--mobile' : '.nodus-slideshow__media--desktop';
        const container = slide.querySelector(mediaClass);
        return container ? [...container.querySelectorAll('video')] : [];
      }

      _hasCurrentVideo() {
        return this._getCurrentVideos().length > 0;
      }

      _syncVideoControls() {
        const hasVid = this._hasCurrentVideo();
        if (this.muteBtn) this.muteBtn.style.display = hasVid ? '' : 'none';
        if (this.playPauseBtn) this.playPauseBtn.style.display = hasVid ? '' : 'none';

        if (hasVid) {
          if (this.muteBtn) this.muteBtn.setAttribute('data-state', this.isMuted ? 'muted' : 'unmuted');
          if (this.playPauseBtn) this.playPauseBtn.setAttribute('data-state', this.isVideoPaused ? 'paused' : 'playing');
        }
      }

      _toggleMute() {
        this.isMuted = !this.isMuted;
        this.slides.forEach(slide => {
          slide.querySelectorAll('video').forEach(v => { v.muted = this.isMuted; });
        });
        this._syncVideoControls();
      }

      _togglePlayPause() {
        this.isVideoPaused = !this.isVideoPaused;
        const vids = this._getCurrentVideos();
        vids.forEach(v => {
          this.isVideoPaused ? v.pause() : v.play().catch(() => {});
        });

        if (this.isVideoPaused) {
          this._clearAutoplay();
        } else if (this.shouldAutoplay && this.slides.length > 1) {
          this._startAutoplay();
        }

        this._syncVideoControls();
      }

      _setupSwipe() {
        let startX = 0;
        let startY = 0;
        let tracking = false;

        this.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          tracking = true;
        }, { passive: true });

        this.addEventListener('touchend', (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
            dx < 0 ? this._next() : this._prev();
            if (this.shouldAutoplay && this.slides.length > 1) this._startAutoplay();
          }
        }, { passive: true });
      }

      _setupSlideLinks() {
        this.slides.forEach((slide) => {
          const link = slide.querySelector('.nodus-slideshow__media-link');
          if (!link || !link.href) return;

          slide.addEventListener('click', (event) => {
            if (!slide.classList.contains('is-active')) return;
            // Native <a> / CTA / controls handle themselves.
            if (event.target.closest('a, button, .nodus-slideshow__content-inner')) return;
            if (event.defaultPrevented) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
              window.open(link.href, '_blank');
              return;
            }
            window.location.href = link.href;
          });
        });
      }

      _onResize() {
        this._syncVideoControls();
        const vids = this._getCurrentVideos();
        vids.forEach(v => {
          if (!this.isVideoPaused) v.play().catch(() => {});
          v.muted = this.isMuted;
        });
      }

      disconnectedCallback() {
        this._clearAutoplay();
      }
    }

    customElements.define('nodus-slideshow', NodusSlideshow);
  }
